import { Link } from "react-router-dom";
import { useThunkDispatch } from "../../store";
import { updateParameterValue } from "../../store/storyline/actions";

export type AnchorWrapperProps = {
    href?: string;
    target?: string;
    children: React.ReactNode;
}

/// Uses React Router <Link> tag for local links and an <a> tag for external links.
/// Allows template developers to use the familiar <a> tag for all links, with local links benefiting from SPA routing (no page reload).
export function AnchorWrapper(props: AnchorWrapperProps) {
    const { href, target, children, ...rest } = props;
    const dispatch = useThunkDispatch();

    // Use cases not supported by <Link< component...
    if (target || !href)
        return <a href={href} target={target} {...rest}>{children}</a>;

    // If the href is relative, we have to use a <Link> tag...
    const to = href.startsWith("/") ? href :
        href.startsWith(document.location.origin) ? href.substring(document.location.origin.length) :
        undefined;

    // Manually handle any side-effects normally performed by full navigation...
    const getClickHandlerForLink = () => {
        const toUrl = new URL(to, window.location.href);

        // Different storyline within the same tenant - router will take care of parameter updates...
        if (window.location.pathname !== toUrl.pathname)
            return undefined;

        // Same storyline - we need to manually broadcast any parameter value updates requested via the query parameters...
        return () => {
            Array.from(toUrl.searchParams).forEach(([key, value]) => {
                dispatch && dispatch(updateParameterValue(key, value));
            });
        }
    };

    // If the href does not fit any of the <Link> use cases, fall back to a raw anchor tag...
    return to ?
        <Link to={to} onClick={getClickHandlerForLink()} {...rest}>{children}</Link> :
        <a href={href} target={target} {...rest}>{children}</a>;
};